import { type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

export interface ConfirmDialogProps {
  open: boolean;
  /** Dialog heading. Defaults to "Are you sure?". */
  title?: string;
  /** Body content explaining the consequence of confirming. */
  message?: ReactNode;
  /** Confirm button label. Defaults to "Delete". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Confirm button variant. Defaults to "danger" (destructive intent). */
  confirmVariant?: 'danger' | 'primary';
  /** In-flight state for the confirm action. */
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * A small confirmation dialog for destructive / irreversible actions, built on
 * the accessible {@link Modal} (focus trap + Escape + restore). Standardizes the
 * "confirm before you destroy authored data" pattern across the app.
 */
export function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  confirmVariant = 'danger',
  loading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={() => !loading && onClose()}
      size="sm"
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-zinc-300">
        {message ?? 'This action cannot be undone.'}
      </div>
    </Modal>
  );
}
